import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { getKitchenDB } from "../../utils/firestore/getKitchen";
import { loadSurchargeConfigsForKitchen } from "../../utils/onlineOrdering/surchargeConfigs";
import {
  Action,
  ActionWithPayload,
  createAction,
  withMatcher,
} from "../../utils/reducer/reducer.utils";
import { RootState } from "../store";
import {
  KITCHEN_ACTION_TYPES,
  Kitchen,
  OnlineOrdersConfigs,
} from "./kitchen.types";
import { kitchenClosedAutoSet } from "../../constants";
import { businessOpenCheck } from "../../utils/closedKitchenCheck";
import { setKitchenTimezone } from "../../utils/timezone";

const SURCHARGE_FETCH_MAX_ATTEMPTS = 3;
const SURCHARGE_FETCH_BASE_DELAY_MS = 1000;

const delay = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const retryWithBackoff = async <T>(
  task: () => Promise<T>,
  maxAttempts: number,
  baseDelayMs: number
): Promise<T> => {
  let lastError: unknown;
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      return await task();
    } catch (error) {
      lastError = error;
      if (attempt < maxAttempts) {
        await delay(baseDelayMs * attempt);
      }
    }
  }
  throw lastError;
};

export type FetchKitchenStart =
  Action<KITCHEN_ACTION_TYPES.FETCH_KITCHEN_START>;
export type FetchKitchenSucess = ActionWithPayload<
  KITCHEN_ACTION_TYPES.FETCH_KITCHEN_SUCCESS,
  Kitchen
>;
export type FetchKitchenFailed = ActionWithPayload<
  KITCHEN_ACTION_TYPES.FETCH_KITCHEN_FAILED,
  Error
  >;

export type SetOnlineOrdersConfigs = ActionWithPayload<
  KITCHEN_ACTION_TYPES.SET_ONLINE_ORDERS_CONFIGS,
  OnlineOrdersConfigs
>;

export const fetchKitchenStart = withMatcher(
  (): FetchKitchenStart =>
    createAction(KITCHEN_ACTION_TYPES.FETCH_KITCHEN_START)
);

export const fetchKitchenSuccess = withMatcher(
  (kitchen: Kitchen): FetchKitchenSucess =>
    createAction(KITCHEN_ACTION_TYPES.FETCH_KITCHEN_SUCCESS, kitchen)
);

export const setOnlineOrdersConfigs = withMatcher(
  (configs: OnlineOrdersConfigs): SetOnlineOrdersConfigs =>
    createAction(KITCHEN_ACTION_TYPES.SET_ONLINE_ORDERS_CONFIGS, configs)
);

export const fetchKitchenFailed = withMatcher(
  (error: Error): FetchKitchenFailed =>
    createAction(KITCHEN_ACTION_TYPES.FETCH_KITCHEN_FAILED, error)
);

export const fetchKitchenAsync =
  (kitchenSlug: string): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchKitchenStart());
    try {
      const kitchenResult = await getKitchenDB(kitchenSlug);
      if (kitchenResult.empty || !("kitchen" in kitchenResult)) {
        dispatch(
          fetchKitchenFailed(
            new Error("Restaurant not found or is no longer available."),
          ),
        );
        return;
      }
      const { kitchen, onlineConfigs } = kitchenResult;

      // Record the venue timezone so all time-dependent logic evaluates against
      // the kitchen's local time rather than the customer's browser clock.
      setKitchenTimezone((kitchen as any)?.timezone);

      // Preserve any surchargeConfigs already in Redux (e.g. loaded by a background
      // fetch on a previous page) so that re-calling fetchKitchenAsync on the checkout
      // page doesn't momentarily clear them before the background fetch fires again.
      const existingSurchargeConfigs = getState().kitchen?.onlineOrdersConfigs?.surchargeConfigs;

      const mergedOnlineConfigs = {
        ...(onlineConfigs || {}),
        surchargeConfigs:
          (onlineConfigs as any)?.surchargeConfigs ||
          (kitchen as any)?.surchargeConfigs ||
          existingSurchargeConfigs,
      } as OnlineOrdersConfigs;

      // console.log(onlineConfigs);
      // if (kitchen?.kitchenOrderStatus === kitchenClosedAutoSet) {
      //   const isOpen = businessOpenCheck(kitchen?.hours?.schedule);
      //   // console.log("============ " + isOpen);
      //   dispatch(setKitchenOpenStatus(isOpen));
      // }

      dispatch(setOnlineOrdersConfigs(mergedOnlineConfigs));

      dispatch(fetchKitchenSuccess(kitchen as unknown as Kitchen));

      // Surcharge configs are non-critical for initial menu render.
      // Load in background with retry and apply when available.
      const kitchenId = kitchen?.kitchenId as string | undefined;
      if (!onlineConfigs?.surchargeConfigs && kitchenId) {
        retryWithBackoff(
          () => loadSurchargeConfigsForKitchen(kitchenId),
          SURCHARGE_FETCH_MAX_ATTEMPTS,
          SURCHARGE_FETCH_BASE_DELAY_MS
        )
          .then(({ surchargeConfigs: configsSurchargeConfigs }) => {
            if (!configsSurchargeConfigs) {
              return;
            }
            // Always use the fresh value from getSurchargeConfigs — this replaces
            // the existingSurchargeConfigs placeholder set above and ensures that
            // a DB change is reflected after the next page load / navigation.
            dispatch(
              setOnlineOrdersConfigs({
                ...mergedOnlineConfigs,
                surchargeConfigs: configsSurchargeConfigs,
              } as OnlineOrdersConfigs)
            );
          })
          .catch((error) => {
            console.error("Background surcharge config fetch failed:", error);
          });
      }
    } catch (error) {
      dispatch(fetchKitchenFailed(error as Error));
    }
  };

export const ensureSurchargeConfigsAsync =
  (
    kitchenId: string,
    onlineOrdersConfig: OnlineOrdersConfigs | null
  ): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    if (!kitchenId || onlineOrdersConfig?.surchargeConfigs) {
      return;
    }

    try {
      const { surchargeConfigs } = await retryWithBackoff(
        () => loadSurchargeConfigsForKitchen(kitchenId),
        SURCHARGE_FETCH_MAX_ATTEMPTS,
        SURCHARGE_FETCH_BASE_DELAY_MS
      );

      if (!surchargeConfigs) {
        return;
      }

      dispatch(
        setOnlineOrdersConfigs({
          ...(onlineOrdersConfig || {}),
          surchargeConfigs,
        } as OnlineOrdersConfigs)
      );
    } catch (error) {
      console.error("Checkout surcharge config fetch failed:", error);
    }
  };
